import {
  MAX_NEW_SKILL_ATTACHMENTS_PER_SAVE,
  MAX_SKILL_ATTACHMENTS,
  parseSkillTextContentType,
  SKILL_MARKDOWN_PATH,
  SKILL_PATH_KINDS,
} from '@aline/common';
import type { SkillPathKind } from '@aline/common';
import type { Apollo } from 'src/gql/generated';

import type { SkillAttachmentChanges } from './skill-attachment-editor';
import type { SkillDocumentItem } from './skill-file-tree';
import {
  formatSkillTreeItemId,
  INSTRUCTIONS_TREE_ITEM,
  parseSkillTreeItemId,
} from './skill-tree-ids';
import type { SkillTreeItem } from './skill-tree-ids';
import { getIsSkillPathInside, getParentPath } from './skill-tree-relocation';
import type { RelocatableSkillTreeItem } from './skill-tree-relocation';

/**
 * Rules for the files of a skill in the editor: which ones open as text, which names and
 * paths a rename, move or new file may take, and where the selection goes after a save.
 */

type SkillAttachment = Apollo.AgentSkillSimpleFragment['attachments'][number];

/**
 * Which tree item shows a file after a save: an unsaved file becomes the saved one it was
 * stored as, and a renamed saved file is found at its new path.
 */
export function getSectionAfterSave({
  attachments,
  current,
  remaining,
  submitted,
}: {
  attachments: SkillAttachment[];
  current: SkillTreeItem;
  remaining: SkillAttachmentChanges;
  submitted: SkillAttachmentChanges;
}): SkillTreeItem {
  switch (current.kind) {
    case 'text':
      return remaining.textFiles?.some(file => file.path === current.path)
        ? current
        : { kind: 'saved', path: current.path };
    case 'saved': {
      const rename = submitted.attachmentRenames?.find(file => file.path === current.path);
      return rename ? { kind: 'saved', path: rename.newPath } : current;
    }
    case 'snapshot': {
      const saved = attachments.find(file => file.sourceDocumentId === current.documentId);
      return saved ? { kind: 'saved', path: saved.path } : current;
    }
    case 'pdf': {
      const saved = attachments.find(file => file.path.split('/').at(-1) === `${current.id}.pdf`);
      return saved ? { kind: 'saved', path: saved.path } : current;
    }
    case 'instructions':
    case 'folder':
    case 'folderDraft':
    case 'fileDraft':
      return current;
  }
}

/**
 * The tree id shown as selected: the section, or SKILL.md once its file is gone.
 *
 * A save's result reaches the skill one render before the editor's own state catches up.
 * In that render a file the save renamed or moved is already at its new path while the
 * section still names the old one. It is found at the new path, so the selection does not
 * fall back to SKILL.md for a frame and remount the open file.
 */
export function getShownSectionId({
  changes,
  documentItems,
  section,
}: {
  changes: SkillAttachmentChanges;
  documentItems: SkillDocumentItem[];
  section: SkillTreeItem;
}): string {
  const shownIds = new Set(documentItems.map(item => item.id));
  const sectionId = formatSkillTreeItemId(section);
  if (shownIds.has(sectionId)) return sectionId;
  const renamedPath =
    section.kind === 'saved'
      ? changes.attachmentRenames?.find(rename => rename.path === section.path)?.newPath
      : undefined;
  const renamedId = renamedPath && formatSkillTreeItemId({ kind: 'saved', path: renamedPath });
  if (renamedId && shownIds.has(renamedId)) return renamedId;
  return formatSkillTreeItemId(INSTRUCTIONS_TREE_ITEM);
}

/**
 * The text file behind the selected item, if it opens in the editor. A file opens by the type
 * it stores, not by its name: a PDF renamed to `.md` is still a PDF. A JSON file only shows a
 * preview, since the Markdown editor would rewrite it.
 */
export function getSelectedTextFile({
  attachments,
  changes,
  selectedItem,
}: {
  attachments: SkillAttachment[];
  changes: SkillAttachmentChanges;
  selectedItem: SkillTreeItem;
}): { path: string; content?: string } | null {
  if (selectedItem.kind === 'text') {
    const file = changes.textFiles?.find(textFile => textFile.path === selectedItem.path);
    return file && getIsEditableContentType(file.contentType)
      ? { content: file.content, path: file.path }
      : null;
  }
  if (selectedItem.kind !== 'saved') return null;
  const file = attachments.find(attachment => attachment.path === selectedItem.path);
  return file && !file.sourceDocumentId && getIsEditableContentType(file.contentType)
    ? { path: file.path }
    : null;
}

/**
 * The first edit of a saved text file, which turns it into an unsaved one at its current
 * path with the type it was stored with. Null when `path` is no saved file still in the
 * skill. `savedPaths` stands in for `retainedAttachmentPaths` until a change sets it.
 */
export function getSavedTextFileEdit({
  attachments,
  changes,
  path,
  savedPaths,
  text,
}: {
  attachments: Pick<SkillAttachment, 'contentType' | 'path'>[];
  changes: SkillAttachmentChanges;
  path: string;
  savedPaths: string[];
  text: string;
}): { changes: SkillAttachmentChanges; newPath: string } | null {
  const retained = changes.retainedAttachmentPaths ?? savedPaths;
  const saved = attachments.find(file => file.path === path);
  if (!retained.includes(path) || !saved) return null;
  const newPath = changes.attachmentRenames?.find(rename => rename.path === path)?.newPath ?? path;
  return {
    changes: {
      ...changes,
      attachmentRenames: changes.attachmentRenames?.filter(rename => rename.path !== path),
      retainedAttachmentPaths: retained.filter(retainedPath => retainedPath !== path),
      textFiles: [
        ...(changes.textFiles ?? []),
        { content: text, contentType: saved.contentType, path: newPath },
      ],
    },
    newPath,
  };
}

/** Which path rules a tree item's new path has to follow. A file's name never decides its type. */
export function getSkillTreeItemPathKind(item: RelocatableSkillTreeItem): SkillPathKind {
  return item.kind === 'folder' ? SKILL_PATH_KINDS.folder : SKILL_PATH_KINDS.file;
}

/**
 * Why adding `adding` files next to `pending` unsaved and `retained` saved ones would break a
 * limit the server enforces, or null when they fit. Checked before any file is read, so the
 * author hears it here rather than from a failed save.
 */
export function getAttachmentLimitError({
  adding,
  pending,
  retained,
}: {
  adding: number;
  pending: number;
  retained: number;
}): string | null {
  if (pending + adding > MAX_NEW_SKILL_ATTACHMENTS_PER_SAVE)
    return `Add at most ${MAX_NEW_SKILL_ATTACHMENTS_PER_SAVE} files at a time.`;
  if (retained + pending + adding > MAX_SKILL_ATTACHMENTS)
    return `A skill can have at most ${MAX_SKILL_ATTACHMENTS} files. Remove one to add another.`;
  return null;
}

/** Why one more new file would break a limit the server enforces, or null when it fits. */
export function getNewFileLimitError(documentItems: SkillDocumentItem[]): string | null {
  const pending = documentItems.filter(file => file.item.kind !== 'saved').length;
  return getAttachmentLimitError({
    adding: 1,
    pending,
    retained: documentItems.length - pending,
  });
}

/**
 * Whether `path` is already used by another file or folder, ignoring case like the server
 * does. `self` is the tree id of the item being renamed or moved: it may keep its own path,
 * and a folder's own contents do not count against it.
 */
export function getIsSkillPathTaken({
  documentItems,
  folders,
  path,
  self,
}: {
  documentItems: SkillDocumentItem[];
  folders: string[];
  path: string;
  self: string | null;
}): boolean {
  const lowerPath = path.toLowerCase();
  const lowerParent = getParentPath(path).toLowerCase();
  const lowerName = (path.split('/').at(-1) ?? path).toLowerCase();
  const selfItem = self === null ? null : parseSkillTreeItemId(self);
  const selfFolder = selfItem?.kind === 'folder' ? selfItem.path : null;
  const getIsInsideSelf = (folder: string) =>
    selfFolder !== null && getIsSkillPathInside(folder, selfFolder);
  return (
    lowerPath === SKILL_MARKDOWN_PATH.toLowerCase() ||
    documentItems.some(
      file =>
        file.id !== self &&
        (file.path.toLowerCase() === lowerPath ||
          lowerPath.startsWith(`${file.path.toLowerCase()}/`) ||
          (file.folder.toLowerCase() === lowerParent && file.label.toLowerCase() === lowerName))
    ) ||
    folders.some(folder => !getIsInsideSelf(folder) && folder.toLowerCase() === lowerPath) ||
    documentItems.some(
      file =>
        !getIsInsideSelf(file.folder) && getIsSkillPathInside(file.folder.toLowerCase(), lowerPath)
    )
  );
}

function getIsEditableContentType(contentType: string) {
  const textType = parseSkillTextContentType(contentType);
  return textType === 'text/markdown' || textType === 'text/plain';
}
